import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import html2pdf from "html2pdf.js";
import { blog } from "@/lib/api";
import InfoproductRenderer from "@/components/InfoproductRenderer";
import ShareButtons from "@/components/ShareButtons";
import { Download, Lock, CheckCircle2, ArrowRight, Clock, Eye, ArrowLeft } from "lucide-react";

const setMeta = (name, content, attr = "name") => {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    setUnlocked(false);
    blog
      .get(slug)
      .then((p) => {
        setPost(p);
        setUnlocked(!p.is_lead_magnet);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    const url = `https://kursosonline.com/blog/${post.slug}`;
    document.title = `${post.title} — Kursos Online`;
    setMeta("description", post.meta_description || post.subtitle || "");
    setMeta("keywords", (post.keywords || []).join(", "));
    setMeta("og:title", post.title, "property");
    setMeta("og:description", post.meta_description || "", "property");
    setMeta("og:type", "article", "property");
    setMeta("og:url", url, "property");

    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.meta_description || post.subtitle || "",
      articleSection: post.category,
      keywords: (post.keywords || []).join(", "),
      datePublished: post.published_at,
      dateModified: post.updated_at,
      author: { "@type": "Organization", name: "Kursos Online" },
      publisher: {
        "@type": "Organization",
        name: "Kursos Online",
        logo: { "@type": "ImageObject", url: "https://kursosonline.com/og-image.png" },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
    };
    let script = document.getElementById("jsonld-article");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "jsonld-article";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
    return () => {
      const s = document.getElementById("jsonld-article");
      if (s) s.remove();
    };
  }, [post]);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await blog.leadMagnet(slug, { email });
      setUnlocked(true);
    } catch {
      setUnlocked(true);
    } finally {
      setSending(false);
    }
  };

  const downloadPdf = () => {
    if (!printRef.current) return;
    setDownloading(true);
    html2pdf()
      .set({
        margin: [12, 12],
        filename: `${post.slug}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      })
      .from(printRef.current)
      .save()
      .then(() => setDownloading(false))
      .catch(() => setDownloading(false));
  };

  if (notFound)
    return (
      <main className="bg-white min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display font-black text-3xl mb-3">Artículo no encontrado</h1>
          <Link to="/blog" className="text-[#4F46E5] font-semibold hover:underline">
            Ver todos los recursos
          </Link>
        </div>
      </main>
    );

  if (!post) return <main className="bg-white min-h-[70vh] flex items-center justify-center text-zinc-500">Cargando...</main>;

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <main data-testid="blog-post-page" className="bg-white min-h-[80vh]">
      <div className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-8">
          <ArrowLeft className="w-4 h-4" /> Todos los recursos
        </Link>

        <div className="flex flex-wrap items-center gap-3 mb-4 text-xs">
          <span className="uppercase tracking-wider font-bold text-[#4F46E5]">{post.category}</span>
          <span className="inline-flex items-center gap-1 text-zinc-500"><Clock className="w-3 h-3" />{(post.published_at || "").slice(0, 10)}</span>
          <span className="inline-flex items-center gap-1 text-zinc-500"><Eye className="w-3 h-3" />{post.views || 0}</span>
        </div>

        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] mb-4">
          {post.title}
        </h1>
        {post.subtitle && <p className="text-lg text-zinc-600 mb-8">{post.subtitle}</p>}

        {post.cover_image && (
          <div className="aspect-[16/9] overflow-hidden bg-zinc-100 mb-10">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        {post.is_lead_magnet && (
          <div data-testid="lead-magnet-card" className="mb-10 bg-gradient-to-br from-indigo-950 via-slate-900 to-emerald-950 text-white p-6 md:p-8">
            <div className="flex items-center gap-2 mb-2">
              <Download className="w-4 h-4 text-[#10B981]" />
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#10B981]">Recurso descargable</p>
            </div>
            <h3 className="font-display font-bold text-xl md:text-2xl mb-3">
              {post.lead_magnet_title || "Descarga esta guía en PDF"}
            </h3>
            {unlocked ? (
              <div>
                <div className="flex items-center gap-2 text-[#10B981] text-sm mb-4">
                  <CheckCircle2 className="w-5 h-5" /> ¡Desbloqueado! Ya puedes descargar el PDF.
                </div>
                <button
                  onClick={downloadPdf}
                  disabled={downloading}
                  data-testid="download-pdf-btn"
                  className="inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#059669] text-white px-6 py-3 font-semibold transition-colors disabled:opacity-60"
                >
                  <Download className="w-4 h-4" /> {downloading ? "Generando PDF..." : "Descargar PDF"}
                </button>
              </div>
            ) : (
              <form onSubmit={submitEmail} className="flex flex-col sm:flex-row gap-2 max-w-md">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  data-testid="lead-magnet-email-input"
                  className="flex-1 bg-slate-800/70 border border-slate-700 px-4 py-3 text-sm outline-none focus:border-[#4F46E5]"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="lead-magnet-submit-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] px-5 py-3 font-semibold text-sm transition-colors disabled:opacity-60"
                >
                  <Lock className="w-4 h-4" /> {sending ? "..." : "Desbloquear PDF"}
                </button>
              </form>
            )}
          </div>
        )}

        <article ref={printRef} data-testid="blog-article-body">
          <InfoproductRenderer content={post.content} />
        </article>

        {post.cta_label && (
          <div data-testid="blog-cta" className="mt-12 bg-zinc-950 text-white p-8 text-center">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-3">Siguiente paso</p>
            <h3 className="font-display font-bold text-2xl mb-5 max-w-lg mx-auto">{post.cta_label}</h3>
            <a
              href={post.cta_url || "/"}
              data-testid="blog-cta-btn"
              className="group inline-flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white px-7 py-4 font-semibold transition-colors"
            >
              {post.cta_label}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        )}

        <ShareButtons title={post.title} url={pageUrl} />
      </div>
    </main>
  );
};

export default BlogPost;
